import { useState, type DragEvent } from 'react';
import { useStore } from '../lib/store.ts';
import { activateTab, closeTab, moveTab, openTab, renameTab, setActivePane, tabs as tabsStore, type Pane, type Tab } from '../state/tabs.ts';
import { Icon, type IconName } from '../ui/Icon.tsx';
import { UnreadDot } from '../ui/primitives.tsx';

// Tabbladenbalk van een paneel: klikken, sluiten, dubbelklik hernoemen, verslepen naar het andere paneel. Een chattabblad van een
// gesprek met een antwoord dat de eigenaar nog niet zag, heeft een stip (ticket 30961, deel 2; `unread` in de tabstaat, zie
// state/chatTurns.ts), tot het gesprek in beeld is.

const ICONS: Record<Tab['kind'], IconName> = { mail: 'mail', chat: 'chat', agenda: 'calendar', rules: 'sliders', dev: 'code', settings: 'settings', knowledge: 'book', usage: 'chart', briefing: 'sun', releases: 'info', excel: 'table', whatsapp: 'whatsapp' };

export function TabBar(props: { pane: Pane; tabs: Record<string, Tab>; active: boolean }) {
  const [editing, setEditing] = useState<string | null>(null);
  const unread = useStore(tabsStore, (s) => s.unread);
  const [over, setOver] = useState(false);

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    const id = e.dataTransfer.getData('text/jerome-tab');
    if (id) moveTab(id, props.pane.id);
  };

  return (
    <div role="tablist" aria-label={`Tabbladen ${props.pane.id === 'left' ? 'links' : 'rechts'}`} className={`h-11 shrink-0 flex items-end gap-1 px-2 border-b border-line bg-paper ${over ? 'bg-tint' : ''}`} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={onDrop} onMouseDown={() => setActivePane(props.pane.id)}>
      {props.pane.tabIds.map((id) => {
        const tab = props.tabs[id];
        if (!tab) return null;
        const active = props.pane.activeTabId === id;
        return (
          <div key={id} role="tab" aria-selected={active} tabIndex={0} draggable onDragStart={(e) => e.dataTransfer.setData('text/jerome-tab', id)} onClick={() => activateTab(id)} onKeyDown={(e) => e.key === 'Enter' && activateTab(id)} onDoubleClick={() => setEditing(id)} className={`group flex items-center gap-2 h-9 pl-3 pr-1.5 rounded-t-lg text-sm cursor-default select-none max-w-[220px] ${active ? `bg-surface text-ink ${props.active ? 'shadow-[inset_0_2px_0_#2f6fed]' : ''}` : 'text-ink-soft hover:bg-tint'}`}>
            <Icon name={ICONS[tab.kind]} size={15} className="shrink-0" />
            {editing === id ? (
              <input autoFocus defaultValue={tab.title} className="w-32 bg-transparent border-b border-accent outline-none" onBlur={(e) => { renameTab(id, e.currentTarget.value); setEditing(null); }} onKeyDown={(e) => { if (e.key === 'Enter') { renameTab(id, e.currentTarget.value); setEditing(null); } if (e.key === 'Escape') setEditing(null); }} onClick={(e) => e.stopPropagation()} />
            ) : (
              <span className="truncate">{tab.title}</span>
            )}
            {tab.kind === 'chat' && typeof tab.state['chatId'] === 'string' && unread?.includes(tab.state['chatId']) ? <UnreadDot /> : null}
            <button type="button" aria-label={`Sluit ${tab.title}`} className="h-6 w-6 rounded flex items-center justify-center text-muted opacity-60 group-hover:opacity-100 hover:bg-tint-2 hover:text-ink" onClick={(e) => { e.stopPropagation(); closeTab(id); }}>
              <Icon name="x" size={13} />
            </button>
          </div>
        );
      })}
      <button type="button" aria-label="Nieuw tabblad" className="h-9 w-9 rounded-t-lg flex items-center justify-center text-muted hover:bg-tint hover:text-ink" onClick={() => openTab('chat', { pane: props.pane.id })}>
        <Icon name="plus" size={16} />
      </button>
    </div>
  );
}
